import { motion, useMotionValue, useTransform, animate } from 'motion/react';
import { useEffect, useState, useMemo } from 'react';
import { limitExtremeRisk } from '@/utils/cancer/risk';
import AnimatedOdometer from '../animated-odometer/animated-odometer';
import {
  calculateDimensions,
  calculateSemicirclePaths,
  calculateStrokeDash,
  generateTickMarks,
  calculateTriangleTransform,
  generateTrianglePoints,
} from './risk-gauge-utils';

interface RiskGaugeProps {
  riskPercentage: number;
  populationAverage: number;
  size?: number;
  strokeWidth?: number;
}

export default function RiskGauge({
  riskPercentage,
  populationAverage,
  size = 301,
  strokeWidth = 14
}: RiskGaugeProps) {
  const [isAnimated, setIsAnimated] = useState(false);

  // Cap visual display at 80% for extreme risks (actual value may be higher)
  const visualRiskPercentage = Math.min(riskPercentage, 80);
  const visualPopulationAverage = Math.min(populationAverage, 80);

  // Apply extreme risk limiting for display text (>80% shows as ">80%")
  const displayRisk = limitExtremeRisk(riskPercentage);
  const displayPopAvg = limitExtremeRisk(populationAverage);

  // Memoize expensive calculations that justify the overhead
  const dimensions = useMemo(() => calculateDimensions(size, strokeWidth), [size, strokeWidth]);
  const { actualSize, actualHeight, outerRadius, innerRadius } = dimensions;

  // Memoize expensive calculations
  const tickMarks = useMemo(() => generateTickMarks(dimensions, strokeWidth), [dimensions, strokeWidth]);
  const semicirclePaths = useMemo(() => calculateSemicirclePaths(dimensions, strokeWidth), [dimensions, strokeWidth]);

  // Simple calculations - use visual percentage for gauge display
  const outerStrokeDash = calculateStrokeDash(outerRadius, visualRiskPercentage);
  const innerStrokeDash = calculateStrokeDash(innerRadius, visualPopulationAverage);
  const trianglePoints = generateTrianglePoints();

  // Motion value for triangle animation along the arc
  const triangleProgress = useMotionValue(0);

  // Transform progress to create smooth arc motion - sweep from 0% to user's risk
  // Use visual percentage (capped at 80%) for triangle position
  const triangleAngle = useTransform(triangleProgress, [0, 1], [
    Math.PI, // Start angle (left side - 0%)
    Math.PI - (visualRiskPercentage / 100) * Math.PI // End angle based on visual risk percentage
  ]);

  // Use utility function for triangle positioning
  const triangleX = useTransform(triangleAngle, (angle) => {
    const transform = calculateTriangleTransform(angle, dimensions, strokeWidth);
    return transform.x;
  });

  const triangleY = useTransform(triangleAngle, (angle) => {
    const transform = calculateTriangleTransform(angle, dimensions, strokeWidth);
    return transform.y;
  });

  const triangleRotation = useTransform(triangleAngle, (angle) => {
    const transform = calculateTriangleTransform(angle, dimensions, strokeWidth);
    return transform.rotation;
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimated(true);
      // Start triangle animation after other animations - animate along arc
      setTimeout(() => {
        animate(triangleProgress, 1, {
          duration: 2,
          ease: [0.22, 1, 0.36, 1]
        });
      }, 500); // Start with outer semicircle (0.5s delay)
    }, 200);
    return () => clearTimeout(timer);
  }, [triangleProgress]);


  return (
    <div className="risk-gauge-container" role="img" aria-labelledby="risk-gauge-title" aria-describedby="risk-gauge-description">
      {/* Screen reader only description */}
      <div id="risk-gauge-title" className="sr-only">Cancer Risk Gauge</div>
      <div id="risk-gauge-description" className="sr-only">
        A visual gauge showing your cancer risk of {typeof displayRisk === 'number' ? `${displayRisk}%` : displayRisk} compared to the population average of {typeof displayPopAvg === 'number' ? `${displayPopAvg}%` : displayPopAvg}.
        Your risk level appears as a colored arc, while the population average is shown as a smaller inner arc.
      </div>

      {/* Legend */}
      <motion.div
        className="risk-gauge-legend"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
        role="group"
        aria-label="Risk gauge legend"
      >
        <div className="risk-gauge-legend-item" role="listitem">
          <div className="risk-gauge-legend-bar-user" aria-hidden="true"></div>
          <span className="risk-gauge-legend-text">Your Cancer Risk</span>
        </div>
        <div className="risk-gauge-legend-item" role="listitem">
          <div className="risk-gauge-legend-bar-population" aria-hidden="true"></div>
          <span className="risk-gauge-legend-text">Pop. Avg.</span>
        </div>
      </motion.div>

      <div className="relative flex items-end justify-center">
        <svg
          width={actualSize}
          height={actualHeight}
          className="risk-gauge-svg"
          aria-hidden="true"
          focusable="false"
        >
        <defs>
          <linearGradient id={`riskGradient-${riskPercentage}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--color-catch-avocado-400)" />
            <stop offset="64.58%" stopColor="var(--color-catch-salmon)" />
          </linearGradient>
        </defs>

        {/* Background semicircles */}
          {/* Outer background */}
          <path
            d={semicirclePaths.outerPath}
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="none"
          />
          {/* Inner background */}
          <path
            d={semicirclePaths.innerPath}
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth={strokeWidth / 3}
            strokeLinecap="round"
            fill="none"
          />
        {/* Tick marks between tracks */}
        {tickMarks.map((tick) => (
          <line
            key={tick.key}
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke={`rgba(255, 255, 255, ${tick.opacity})`}
            strokeWidth="1"
          />
        ))}

        {/* Inner Population Average semicircle */}
        <motion.path
          d={semicirclePaths.innerPath}
          stroke="var(--color-catch-light-blue)"
          strokeWidth={strokeWidth / 3}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={innerStrokeDash.semicircleLength}
          initial={{ strokeDashoffset: innerStrokeDash.semicircleLength }}
          animate={{
            strokeDashoffset: isAnimated ? innerStrokeDash.offset : innerStrokeDash.semicircleLength
          }}
          transition={{
            duration: 1.5,
            ease: [0.22, 1, 0.36, 1],
            delay: 0.5
          }}
        />

        {/* Outer User Risk semicircle */}
        <motion.path
          d={semicirclePaths.outerPath}
          stroke={`url(#riskGradient-${riskPercentage})`}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={outerStrokeDash.semicircleLength}
          initial={{ strokeDashoffset: outerStrokeDash.semicircleLength }}
          animate={{
            strokeDashoffset: isAnimated ? outerStrokeDash.offset : outerStrokeDash.semicircleLength
          }}
          transition={{
            duration: 2,
            ease: [0.22, 1, 0.36, 1],
            delay: 0.5
          }}
        />

        {/* Triangle indicator for user risk */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: isAnimated ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.polygon
            points={trianglePoints}
            fill="var(--color-catch-salmon)"
            style={{
              x: triangleX,
              y: triangleY,
              rotate: triangleRotation,
            }}
          />
        </motion.g>
      </svg>

      {/* Center content */}
      <div className="risk-gauge-center-content" role="group" aria-label={`Cancer risk percentage: ${typeof displayRisk === 'number' ? `${displayRisk}%` : displayRisk}`}>
        <AnimatedOdometer
          value={displayRisk}
          className="risk-gauge-percentage dashboard-pct"
          duration={2}
          aria-label={`Your cancer risk is ${typeof displayRisk === 'number' ? `${displayRisk} percent` : displayRisk}`}
        />
        <motion.div
          className="risk-gauge-label"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.5 }}
          aria-hidden="true"
        >
          Lifetime Risk Score
        </motion.div>
      </div>
    </div>
    </div>
  );
}
